<div class="single-user layout-spacing">
    @if (!isLoadingUser()) {
        <div class="single-user__header">
            <div class="header__left">
                <div class="header__title-row">
                    <h1 class="header__name">
                        {{ userInfo()?.firstName + ' ' + userInfo()?.lastName }}
                    </h1>
                    <span class="user-badge">{{ clientType() | uppercase }}</span>
                </div>
                <p class="header__subtitle">
                    Created
                    {{ $safeNavigationMigration(userInfo()?.createdAt) | date: 'MMMM dd, yyyy' }} by
                    {{ createdByDisplayName() }}
                </p>
            </div>
            <div class="header__right">
                @if (!isEditingUser()) {
                    <ntv-button (buttonClick)="onEditUserClick()" variant="accent" size="xl">
                        Edit User
                    </ntv-button>
                } @else {
                    <div class="header__actions">
                        <ntv-button (buttonClick)="cancelEdit()" variant="secondary" size="xl">
                            Cancel
                        </ntv-button>
                        <ntv-button
                            (buttonClick)="saveInformation()"
                            variant="accent"
                            size="xl"
                            [disabled]="!canSaveChanges()">
                            Save Changes
                        </ntv-button>
                    </div>
                }
            </div>
        </div>

        <div class="single-user__tabs">
            <ntv-tabs
                [tabs]="tabs"
                [activeIndex]="activeTab()"
                (tabChange)="onTabChange($event)"></ntv-tabs>
        </div>

        <div class="single-user__content">
            <form class="single-user__form" [formGroup]="form()">
                <!-- Edit User Info Section -->
                <ntv-card variant="elevated" rounded="xl" shadow="sm">
                    <div class="content-card">
                        <div class="card-header">
                            <div class="card-header__icon">
                                <svg width="24" height="24">
                                    <use [attr.href]="spritePath + '#' + icons.user"></use>
                                </svg>
                            </div>
                            <div class="card-header__info">
                                <h2 class="card-title">
                                    {{ isEditingUser() ? 'Edit User Info' : 'User Info' }}
                                </h2>
                                <p class="card-subtitle">
                                    @if (isEditingUser()) {
                                        Modify basic user info and save changes.
                                    } @else {
                                        Details are read-only. Use Edit User to update this profile.
                                    }
                                </p>
                            </div>
                        </div>

                        <div
                            class="user-form-grid"
                            [class.user-form-grid--display-only]="!isEditingUser()">
                            @for (
                                userInfoField of userInfoFields();
                                track userInfoField.fieldName
                            ) {
                                <div class="form-field">
                                    <ntv-input
                                        [label]="userInfoField.label"
                                        [formControlName]="userInfoField.fieldName"
                                        [maxLength]="
                                            isContactNumberField(userInfoField.fieldName)
                                                ? 10
                                                : null
                                        "
                                        (keydown)="
                                            onNumericKeydown($event, userInfoField.fieldName)
                                        "
                                        (blur)="onNumericBlur(userInfoField.fieldName)"
                                        [error]="getFieldErrorMessage(userInfoField.fieldName)[0]"
                                        placeholder="Placeholder"></ntv-input>
                                </div>
                            }
                        </div>
                    </div>
                </ntv-card>
            </form>

            <!-- TODO: Uncomment when ready to implement
                    <ntv-card variant="elevated" rounded="xl" shadow="sm">
                        <div class="content-card">
                            <div class="card-header flex-between">
                                <div class="card-header__info">
                                    <h2 class="card-title">Change Password</h2>
                                    <p class="card-subtitle">Change this user's password</p>
                                </div>
                                <app-button iconKey="copy" variant="accent" size="sm" gap="gap-2" />
                            </div>

                            <div class="user-form-grid">
                                @for (
                                    userPasswordField of userPasswordFields();
                                    track userPasswordField.fieldName
                                ) {
                                    <div class="form-field">
                                        <ntv-input
                                            [label]="userPasswordField.label"
                                            [formControlName]="userPasswordField.fieldName"
                                            (focus)="onFocus(userPasswordField.fieldName)"
                                            [error]="
                                                getFieldErrorMessage(userPasswordField.fieldName)[0]
                                            "
                                            placeholder="Placeholder"
                                            type="password"></ntv-input>
                                    </div>
                                }
                            </div>

                            <div class="password-requirements">
                                <div class="requirements-header">
                                    <svg width="20" height="20">
                                        <use [attr.href]="spritePath + '#' + icons.shield"></use>
                                    </svg>
                                    <span class="requirements-title">Password Requirements:</span>
                                </div>
                                <ul class="requirements-list">
                                    <li>At least 8 characters long</li>
                                    <li>Contains uppercase and lowercase letters</li>
                                    <li>Contains at least one number</li>
                                    <li>Contains at least one special character</li>
                                </ul>
                            </div>

                            @if (showDealerManagement()) {
                                <div class="single-user__dealers">
                                    <div class="single-user__dealers&#45;&#45;left">
                                        <div class="card-header">
                                            <div class="card-header__info">
                                                <h2 class="card-title">Manage Dealers</h2>
                                                <p class="card-subtitle">
                                                    Change dealer admin's associate dealers
                                                </p>
                                            </div>
                                        </div>
                                        <app-autocomplete
                                            [loading]="isSearchingDealer()"
                                            [options]="organizationDealers()"
                                            [(selectedOptions)]="selectedDealers"
                                            [config]="{
                                                placeholder: 'Select a dealer',
                                                searchable: true,
                                                multiple: true,
                                            }"
                                            (onSearch)="onDealerSearch($event)"
                                            (selectedOptionsChange)="onDealerSelect($event)" />
                                    </div>
                                    <div class="single-user__dealers&#45;&#45;right">
                                        <div class="card-header">
                                            <div class="card-header__info">
                                                <h2 class="card-title">
                                                    No Selected Dealer Name(s)
                                                </h2>
                                                <p class="card-subtitle">
                                                    This field displays the dealer name or names
                                                    that have been selected
                                                </p>
                                            </div>
                                        </div>

                                        <div class="single-user__dealers&#45;&#45;right__selected">
                                            @for (dealer of selectedDealers(); track dealer.value) {
                                                <app-removable-chip
                                                    [label]="dealer.label"
                                                    (remove)="removeDealer(dealer.value)">
                                                </app-removable-chip>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }

                            <div class="single-user__email">
                                <div class="email__description">
                                    <h2 class="email__description&#45;&#45;title">
                                        Receive Email Notification:
                                    </h2>
                                    <p class="email__description&#45;&#45;subtitle">
                                        Email Notifications about account Activity
                                    </p>
                                </div>
                                <ntv-toggle-button
                                    [checked]="true"
                                    color="accent_color"></ntv-toggle-button>
                            </div>
                        </div>
                    </ntv-card>
                    -->
        </div>
    } @else {
        <!-- Skeleton Loader -->
        <div class="single-user__header">
            <div class="header__left">
                <div class="header__title-row">
                    <ntv-skeleton width="250px" height="32px" borderRadius="8px"></ntv-skeleton>
                    <ntv-skeleton
                        width="100px"
                        height="26px"
                        borderRadius="6px"
                        class="ml-4"></ntv-skeleton>
                </div>
                <div class="header__subtitle" style="margin-top: 8px">
                    <ntv-skeleton width="320px" height="20px" borderRadius="4px"></ntv-skeleton>
                </div>
            </div>
            <div class="header__right">
                <ntv-skeleton variant="button" width="140px" height="44px"></ntv-skeleton>
            </div>
        </div>

        <div class="single-user__tabs" style="padding-top: 8px; padding-bottom: 8px">
            <ntv-skeleton width="200px" height="40px" borderRadius="6px"></ntv-skeleton>
        </div>

        <div class="single-user__content">
            <ntv-card variant="elevated" rounded="xl" shadow="sm">
                <div class="content-card">
                    <div class="card-header">
                        <ntv-skeleton variant="avatar" width="40px" height="40px"></ntv-skeleton>
                        <div class="card-header__info" style="margin-left: 16px; width: 100%">
                            <ntv-skeleton width="150px" height="24px" class="mb-2"></ntv-skeleton>
                            <ntv-skeleton width="300px" height="16px"></ntv-skeleton>
                        </div>
                    </div>
                    <div class="user-form-grid" style="margin-top: 24px">
                        @for (i of [1, 2, 3, 4]; track i) {
                            <ntv-skeleton
                                width="100%"
                                height="40px"
                                borderRadius="8px"></ntv-skeleton>
                        }
                    </div>
                </div>
            </ntv-card>

            <ntv-card variant="elevated" rounded="xl" shadow="sm">
                <div class="content-card">
                    <div class="card-header flex-between">
                        <div class="card-header__info">
                            <ntv-skeleton width="150px" height="24px" class="mb-2"></ntv-skeleton>
                            <ntv-skeleton width="250px" height="16px"></ntv-skeleton>
                        </div>
                        <ntv-skeleton variant="button" width="100px" height="36px"></ntv-skeleton>
                    </div>
                    <div class="user-form-grid" style="margin-top: 24px">
                        @for (i of [1, 2]; track i) {
                            <ntv-skeleton
                                width="100%"
                                height="40px"
                                borderRadius="8px"></ntv-skeleton>
                        }
                    </div>
                </div>
            </ntv-card>
        </div>
    }
</div>

<!-- Update success modal -->
@if (isUserUpdated()) {
    <app-confirmation-modal
        [isVisible]="isUserUpdated()"
        [size]="'small'"
        [title]="'User Updated'"
        [message]="'User has been updated successfully'"
        [confirmText]="'Close'"
        (confirm)="closeUpdatedModal()"
        (modalClose)="closeUpdatedModal()"></app-confirmation-modal>
}
